import React, {useCallback, useMemo, useState} from 'react';
import cx from 'classnames';
import {
    Button,
    Flex,
    Icon,
    noop,
    PriorityIcon,
    Text,
    Title,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import FilterBox from '../../../../../../../features/AppSearches/componenets/FilterBox/FilterBox';
import {ISearchCondition} from '../../../../../../AppSearches/types/filters';
import {Form} from '../../../../../../../stores/Form/Form';
import {serializeField} from '../../../../../common/serializeFiels';
import {SortIcon} from '../../../../../../../components/Common/SortIcon';
import {texts} from '../../texts';
import {priority} from '@priority-software/priority-proto-api/lib';
import {patchFetchConditions} from '../../../../../utils/filters';
import {PatchFilter} from '../../index';
import {CheckedValue, ToggleBtnLabel} from '../../../../../../../components/ToggleButton/enum';
import {ToggleButton} from '../../../../../../../components/ToggleButton';
import {createDataTestId} from '../../../../../../../utils/dataTestId';
import {ColumnType} from '../../../../../../AppSearches/types/column';
import {ModernTooltip} from '../../../../../../../components/Common/Tooltip/ModernTooltip';
import styles from './styles.module.scss';

type Props = {
    form?: Form;
    componentClasses?: string;
    filter?: PatchFilter;
    onRemoveFilter: (id: number) => void;
    updateFilter: (filter: PatchFilter, columnId: number) => void;
};

export const FilterItem = ({
    componentClasses,
    filter,
    form,
    onRemoveFilter,
    updateFilter
}: Props) => {
    const columnByID = form?.getColumnByID(Number(filter.Condition[0]?.columnID));
    const columnId = columnByID.id;

    const column = useMemo(() => serializeField(columnByID), [columnByID]);

    const [isSort, setIsSort] = useState<boolean>(!!filter.sort);
    const [isDesc, setIsDesc] = useState<boolean>(!!filter.sort?.Desc);
    const [checkedOperator, setCheckedOperator] = useState<CheckedValue>(CheckedValue.right);

    const handleToggleOperator = useCallback(
        (value: CheckedValue) => {
            updateFilter(
                {
                    ...filter,
                    Operator:
                        value === CheckedValue.left
                            ? priority.netitems.shared.LogicalOperator.And
                            : priority.netitems.shared.LogicalOperator.Or
                },
                columnId
            );
            setCheckedOperator(value);
        },
        [updateFilter, filter, columnId]
    );

    const handleAddCondition = useCallback(() => {
        if (filter.Condition.length < 2) {
            updateFilter(
                {
                    ...filter,
                    Condition: [...filter.Condition, ...patchFetchConditions(columnByID)]
                },
                columnByID.id
            );
        }
    }, [columnByID, updateFilter, filter]);

    const handleSort = useCallback(() => {
        const newIsSort = !isSort;
        const sort = newIsSort ? {ColumnID: columnId, Desc: false} : undefined;
        updateFilter({...filter, sort}, columnId);
        setIsDesc(false);
        setIsSort(newIsSort);
    }, [columnId, updateFilter, filter, isSort]);

    const handleSortDesc = useCallback(() => {
        updateFilter({...filter, sort: {ColumnID: columnId, Desc: !isDesc}}, columnId);
        setIsDesc(prev => !prev);
    }, [columnId, updateFilter, filter, isSort]);

    const onRemoveCondition = useCallback(
        (columnID: number, index: number) => {
            if (filter.Condition.length > 1) {
                return updateFilter(
                    {
                        ...filter,
                        Condition: filter.Condition.filter((cond, idx) => idx !== index)
                    },
                    columnID
                );
            }
            return onRemoveFilter(columnID);
        },
        [updateFilter, onRemoveFilter, filter]
    );

    const handleUpdateFilter = useCallback(
        (cond: ISearchCondition, idx: number) => {
            updateFilter(
                {
                    ...filter,
                    Condition: [
                        ...filter.Condition.slice(0, idx),
                        {
                            ...filter.Condition[idx],
                            values: cond.values,
                            operator: cond.operator,
                            dateEnum: cond.dateEnum
                        },
                        ...filter.Condition.slice(idx + 1)
                    ]
                },
                columnId
            );
        },
        [updateFilter, filter, columnId]
    );

    const isBool = filter?.Condition?.some(c => c.type === ColumnType.Bool);

    return (
        <div
            className={cx(styles.conditionsItemWrapper, componentClasses)}
            {...createDataTestId('advanced-search-filter-container', false)}
        >
            <Flex justifyContent="space-between" alignItems="center">
                <Title
                    skin="h6"
                    text={column.title}
                    {...createDataTestId('advanced-search-filter-title', true)}
                />
                <Icon
                    size="small"
                    icon={PriorityIcon.closeBig}
                    onClick={() => onRemoveCondition(columnId, 0)}
                    skin="standart"
                    style={{zIndex: 10}}
                />
            </Flex>
            {filter.Condition.map((cond, idx) => {
                return (
                    <div key={cond.id} className={styles.filterBoxWrapper}>
                        <FilterBox
                            isCancelBtnOnHover={filter.Condition.length > 1}
                            withTitle={false}
                            dropdownWidth={540}
                            componentClasses={styles.filterBox}
                            contentClasses={styles.filterBoxContent}
                            withSort={false}
                            onRemoveFilter={id => onRemoveCondition(id as number, idx)}
                            onUpdateFilter={condition => handleUpdateFilter(condition, idx)}
                            scrollToBottom={noop}
                            filter={cond}
                            column={column}
                            session={form.session}
                        />

                        {!isBool && idx < filter.Condition.length - 1 ? (
                            <Flex justifyContent="space-between">
                                <ToggleButton
                                    checked={checkedOperator}
                                    onChange={handleToggleOperator}
                                    left={ToggleBtnLabel.And}
                                    right={ToggleBtnLabel.Or}
                                />
                                <Icon
                                    size="small"
                                    icon={PriorityIcon.closeBig}
                                    onClick={() => onRemoveCondition(columnId, idx + 1)} // remove next condition
                                    skin="standart"
                                />
                            </Flex>
                        ) : (
                            <span></span>
                        )}
                    </div>
                );
            })}

            <Flex
                justifyContent="space-between"
                alignItems="center"
                {...createDataTestId('advanced-search-filter-footer-container', true)}
            >
                {!isBool ? (
                    <Button
                        disabled={filter.Condition.length === 2}
                        onClick={handleAddCondition}
                        componentClasses={styles.AddBtn}
                        skin="secondary"
                    >
                        <Icon
                            icon={PriorityIcon.add}
                            skin="primary"
                            componentClasses={styles.iconPlus}
                        />
                    </Button>
                ) : (
                    <span> </span>
                )}

                <Flex inline>
                    <div>
                        <Text skin="label-1" textColor="primary-700" endPadding>
                            {texts.filterSort}
                        </Text>
                    </div>

                    <ModernTooltip tooltip={texts.filterSortTooltip} position="top-end">
                        <ToggleSwitch
                            checked={isSort}
                            onChange={handleSort}
                            {...createDataTestId('advanced-search-switch-sort', true)}
                        />
                    </ModernTooltip>

                    <SortIcon
                        disabled={!isSort}
                        isDesc={isDesc}
                        onClick={handleSortDesc}
                        componentClasses={styles.sortIcon}
                    />
                </Flex>
            </Flex>
        </div>
    );
};
